import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { CreateFromSourceTemplateConfig, CreateFromSourceLineColumn, CreateFromSourceField } from './template/MasterDocumentTemplate.types';
import type { IMasterDocumentAdapter, PendingSourceLine } from './adapter/MasterDocumentAdapter.types';
import {
  useCreateFromSource,
  type CreateFromSourceMode,
} from './smart/useCreateFromSource';
import { Loading } from './components/loading';
import { useMasterDocumentTranslation } from './i18n';

export interface CreateFromSourceModalProps {
  open: boolean;
  mode: CreateFromSourceMode;
  config: CreateFromSourceTemplateConfig;
  adapter: IMasterDocumentAdapter;
  /** Requerido en modo `append`. */
  documentId?: string;
  operationCenterId?: string;
  /** Tercero fijado del header (modo `append`); filtra orígenes y llega read-only. */
  lockedThirdPartyId?: string;
  userPermissions?: string[] | null;
  formatCurrency?: (value: number) => string;
  formatNumber?: (value: number) => string;
  onClose: () => void;
  onCreated?: (documentId: string) => void;
  onAppended?: (addedLineIds: string[]) => void;
  onFreeCapture?: (route: string) => void;
}

const OVERLAY_CLASS = 'fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]';
const PANEL_CLASS = clsx(
  'fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
  'w-[calc(100%-32px)] max-w-[860px] max-h-[calc(100vh-64px)] flex flex-col',
  'bg-bg-primary rounded-2xl',
  'shadow-lg animate-[fadeIn_150ms_ease-out]',
  'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
);
const HEADER_CELL = 'px-3 py-2 text-xs font-bold uppercase tracking-wide text-content-secondary dark:text-dark-content-secondary';
const CELL = 'px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary align-top';
const LABEL_CLASS = 'block text-sm font-semibold text-content-primary dark:text-dark-content-primary mb-1';
const INPUT_CLASS = clsx(
  'w-full rounded-lg px-3 py-2 text-sm',
  'border border-border-primary bg-bg-primary text-content-primary',
  'placeholder:text-content-tertiary transition-colors duration-150',
  'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
  'dark:bg-dark-bg-primary dark:border-dark-border-primary dark:text-dark-content-primary',
  'dark:placeholder:text-dark-content-secondary dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
);

function readCell(line: PendingSourceLine, key: string): unknown {
  if (key in line) return (line as unknown as Record<string, unknown>)[key];
  return line.extra?.[key];
}

export function CreateFromSourceModal(props: CreateFromSourceModalProps) {
  const { open, config, formatCurrency, formatNumber, onClose } = props;
  const { t } = useMasterDocumentTranslation();

  const s = useCreateFromSource({
    open: props.open,
    mode: props.mode,
    config: props.config,
    adapter: props.adapter,
    documentId: props.documentId,
    operationCenterId: props.operationCenterId,
    lockedThirdPartyId: props.lockedThirdPartyId,
    userPermissions: props.userPermissions,
    onCreated: props.onCreated,
    onAppended: props.onAppended,
    onFreeCapture: props.onFreeCapture,
  });

  if (!open) return null;

  const i18n = config.i18n;
  const fmtNum = (v: number | undefined): string => (v == null ? '—' : formatNumber ? formatNumber(v) : String(v));
  const fmtCur = (v: number | undefined): string => (v == null ? '—' : formatCurrency ? formatCurrency(v) : String(v));

  const visibleColumns = config.lineSelection.columns.filter((c) => !c.financial || s.canSeeFinancial);

  const title = (() => {
    switch (s.step) {
      case 'origin': return i18n?.originSelectorTitle ?? t('master_document.createFromSource.originSelectorTitle');
      case 'sources': return i18n?.step1Title ?? t('master_document.createFromSource.step1Title');
      case 'lines': return i18n?.step2Title ?? t('master_document.createFromSource.step2Title');
      case 'confirm': return i18n?.confirmTitle ?? t('master_document.chrome.confirm');
    }
  })();

  function renderColumnCell(line: PendingSourceLine, col: CreateFromSourceLineColumn) {
    const raw = readCell(line, col.key);
    let text: string;
    if (raw == null) text = '—';
    else if (col.format === 'currency') text = fmtCur(Number(raw));
    else if (col.format === 'number') text = fmtNum(Number(raw));
    else text = String(raw);
    return (
      <td
        key={col.key}
        className={clsx(CELL, col.align === 'right' && 'text-right tabular-nums', col.align === 'center' && 'text-center', col.mono && 'font-mono')}
      >
        {text}
      </td>
    );
  }

  // Renderiza un campo de formulario config-driven (defaultsStep o createHeaderFields).
  function renderField(field: CreateFromSourceField, value: unknown, onChange: (v: unknown) => void, idPrefix: string) {
    const id = `${idPrefix}-${field.key}`;
    const options = field.optionsEntity
      ? (s.selectOptions[field.optionsEntity] ?? field.options ?? [])
      : (field.options ?? []);
    return (
      <div key={field.key}>
        <label htmlFor={id} className={LABEL_CLASS}>
          {field.label}
          {field.required && <span className="ml-0.5 text-red-600 dark:text-red-400" aria-hidden="true"> *</span>}
        </label>
        {field.type === 'select' ? (
          <select id={id} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} className={INPUT_CLASS}>
            <option value="">{field.placeholder ?? '—'}</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input
            id={id}
            type={field.type}
            value={String(value ?? '')}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={INPUT_CLASS}
          />
        )}
      </div>
    );
  }

  const headerFields = config.createHeaderFields ?? [];
  const showFormBlock = !!config.defaultsStep || (props.mode === 'create' && headerFields.length > 0);

  return (
    <>
      <div className={OVERLAY_CLASS} aria-hidden="true" />
      <div className={PANEL_CLASS} role="dialog" aria-modal="true" aria-labelledby="cfs-modal-title">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-border-primary dark:border-dark-border-primary">
          <h2 id="cfs-modal-title" className="text-base font-bold text-content-primary dark:text-dark-content-primary pr-6">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-background-secondary text-content-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 dark:hover:bg-dark-bg-secondary"
            aria-label={t('master_document.chrome.close')}
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Paso 0 — selector de origen */}
          {s.step === 'origin' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => s.setOriginChoice('source')}
                className={clsx(
                  'text-left rounded-xl border p-4 transition-all duration-150',
                  'border-border-primary hover:border-primary-custom-400 hover:bg-background-secondary',
                  'dark:border-dark-border-primary dark:hover:bg-dark-bg-secondary',
                )}
              >
                <div className="font-semibold text-content-primary dark:text-dark-content-primary">
                  {i18n?.fromSourceOption ?? t('master_document.createFromSource.fromSourceOption')}
                </div>
              </button>
              <button
                type="button"
                disabled={!s.canFreeCapture}
                onClick={() => s.setOriginChoice('free')}
                className={clsx(
                  'text-left rounded-xl border p-4 transition-all duration-150',
                  'border-border-primary hover:border-primary-custom-400 hover:bg-background-secondary',
                  'dark:border-dark-border-primary dark:hover:bg-dark-bg-secondary',
                  !s.canFreeCapture && 'opacity-50 cursor-not-allowed',
                )}
              >
                <div className="font-semibold text-content-primary dark:text-dark-content-primary">
                  {i18n?.freeCaptureOption ?? t('master_document.createFromSource.freeCaptureOption')}
                </div>
              </button>
            </div>
          )}

          {/* Paso 1 — selección de orígenes */}
          {s.step === 'sources' && (
            <>
              {s.candidatesLoading && (
                <Loading
                  size="md"
                  label={i18n?.loadingSources ?? t('master_document.createFromSource.loadingSources')}
                />
              )}
              {!s.candidatesLoading && s.candidatesError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 dark:bg-red-950/20 dark:border-red-800 dark:text-red-300" role="alert">
                  {s.candidatesError}
                </div>
              )}
              {!s.candidatesLoading && !s.candidatesError && s.candidates.length === 0 && (
                <div className="py-8 px-4 text-center text-sm text-content-secondary dark:text-dark-content-secondary border border-dashed border-border-primary dark:border-dark-border-primary rounded-lg">
                  {i18n?.emptyState ?? t('master_document.createFromSource.emptyState')}
                </div>
              )}
              {!s.candidatesLoading && s.candidates.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border-primary dark:border-dark-border-primary">
                        <th className={clsx(HEADER_CELL, 'w-11')} aria-label={t('master_document.crossing.selectRow')} />
                        <th className={clsx(HEADER_CELL, 'text-left')}>{i18n?.sourceColumns?.document ?? t('master_document.createFromSource.columns.document')}</th>
                        <th className={clsx(HEADER_CELL, 'text-left')}>{i18n?.sourceColumns?.thirdParty ?? t('master_document.createFromSource.columns.thirdParty')}</th>
                        <th className={clsx(HEADER_CELL, 'text-left')}>{i18n?.sourceColumns?.date ?? t('master_document.createFromSource.columns.date')}</th>
                        <th className={clsx(HEADER_CELL, 'text-left')}>{i18n?.sourceColumns?.state ?? t('master_document.createFromSource.columns.state')}</th>
                        <th className={clsx(HEADER_CELL, 'text-right')}>{i18n?.sourceColumns?.total ?? t('master_document.createFromSource.columns.total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.candidates.map((c) => {
                        const reason = s.incompatibleReasons[c.documentId];
                        const disabled = !!reason;
                        const selected = s.selectedSourceIds.includes(c.documentId);
                        return (
                          <tr
                            key={c.documentId}
                            className={clsx('border-b border-border-primary/60 dark:border-dark-border-primary/60', disabled && 'opacity-50')}
                            title={reason}
                          >
                            <td className={clsx(CELL, 'text-center')}>
                              <input
                                type="checkbox"
                                checked={selected}
                                disabled={disabled}
                                onChange={() => s.toggleSource(c.documentId)}
                                className={clsx('w-4 h-4 rounded border-border-primary accent-primary-custom-600', disabled && 'cursor-not-allowed')}
                                aria-label={t('master_document.crossing.pending.selectDocumentAria', { document: c.documentNumber })}
                              />
                            </td>
                            <td className={clsx(CELL, 'whitespace-nowrap font-medium')}>{c.documentNumber}</td>
                            <td className={CELL}>{c.thirdPartyName ?? '—'}</td>
                            <td className={clsx(CELL, 'whitespace-nowrap')}>{c.documentDate ?? '—'}</td>
                            <td className={CELL}>{c.state ?? '—'}</td>
                            <td className={clsx(CELL, 'text-right whitespace-nowrap tabular-nums')}>{fmtCur(c.netTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {!s.canMultiSelect && (
                    <p className="mt-2 text-xs text-content-secondary dark:text-dark-content-secondary">
                      {i18n?.singleSelectHint ?? t('master_document.createFromSource.singleSelectHint')}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Paso 2 — líneas + datos por defecto */}
          {s.step === 'lines' && (
            <>
              {s.linesLoading && (
                <Loading
                  size="md"
                  label={i18n?.loadingLines ?? t('master_document.createFromSource.loadingLines')}
                />
              )}
              {!s.linesLoading && s.linesError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 dark:bg-red-950/20 dark:border-red-800 dark:text-red-300 mb-3" role="alert">
                  {s.linesError}
                </div>
              )}
              {!s.linesLoading && !s.linesError && s.pendingLines.length === 0 && (
                <div className="py-8 px-4 text-center text-sm text-content-secondary dark:text-dark-content-secondary border border-dashed border-border-primary dark:border-dark-border-primary rounded-lg">
                  {i18n?.emptyLines
                    ?? t('master_document.createFromSource.emptyLines', {
                      count: s.selectedSourceIds.length,
                    })}
                </div>
              )}
              {!s.linesLoading && s.pendingLines.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border-primary dark:border-dark-border-primary">
                        <th className={clsx(HEADER_CELL, 'w-11')} aria-label={t('master_document.crossing.selectRow')} />
                        {visibleColumns.map((col) => (
                          <th key={col.key} className={clsx(HEADER_CELL, col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left')}>
                            {col.header}
                          </th>
                        ))}
                        <th className={clsx(HEADER_CELL, 'text-left')}>{i18n?.quantityColumn ?? t('master_document.createFromSource.quantityColumn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.pendingLines.map((line) => {
                        const selected = s.selectedLineIds.includes(line.lineId);
                        const err = s.lineErrors[line.lineId];
                        const qty = s.quantities[line.lineId];
                        const pending = Number(readCell(line, config.lineSelection.pendingField ?? 'quantityPending') ?? line.quantityPending);
                        return (
                          <tr key={line.lineId} className="border-b border-border-primary/60 dark:border-dark-border-primary/60">
                            <td className={clsx(CELL, 'text-center')}>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => s.toggleLine(line.lineId, e.target.checked)}
                                className="w-4 h-4 rounded border-border-primary accent-primary-custom-600"
                                aria-label={t('master_document.crossing.pending.selectLineAria', { item: line.itemCode ?? line.lineId })}
                              />
                            </td>
                            {visibleColumns.map((col) => renderColumnCell(line, col))}
                            <td className={CELL}>
                              <input
                                type="number"
                                inputMode="decimal"
                                min={0}
                                max={pending}
                                value={selected && qty != null ? qty : ''}
                                disabled={!selected}
                                onChange={(e) => s.setQuantity(line.lineId, e.target.value === '' ? NaN : Number(e.target.value))}
                                placeholder={selected ? fmtNum(pending) : ''}
                                aria-invalid={err ? true : undefined}
                                className={clsx(
                                  'w-28 px-2 py-1 rounded-md text-sm tabular-nums border bg-bg-primary dark:bg-dark-bg-primary',
                                  'text-content-primary dark:text-dark-content-primary',
                                  'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 dark:focus:ring-dark-border-custom',
                                  err ? 'border-red-500 dark:border-red-400' : 'border-border-primary dark:border-dark-border-primary',
                                  !selected && 'opacity-50 cursor-not-allowed',
                                )}
                              />
                              {err && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{err}</p>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Datos por defecto (por línea) + campos de header (create), config-driven */}
              {showFormBlock && (
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {config.defaultsStep?.fields.map((field) =>
                    renderField(field, s.defaults[field.key], (v) => s.setDefault(field.key, v), 'cfs-def'),
                  )}
                  {props.mode === 'create' && headerFields.map((field) =>
                    renderField(field, s.headerValues[field.key], (v) => s.setHeaderValue(field.key, v), 'cfs-hdr'),
                  )}
                </div>
              )}
            </>
          )}

          {/* Paso 3 — confirmación de efectos */}
          {s.step === 'confirm' && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-content-primary dark:text-dark-content-primary">
                {/* Dos contadores en una frase: cada sustantivo se pluraliza por
                    separado y entra como {linesLabel}/{sourcesLabel}. {lines} y
                    {sources} siguen siendo números, para no romper los overrides
                    que el host ya tenga escritos. */}
                {(i18n?.confirmSummary ?? t('master_document.createFromSource.confirmSummary'))
                  .replaceAll('{lines}', String(s.selectedLineIds.length))
                  .replaceAll('{sources}', String(s.selectedSourceIds.length))
                  .replaceAll(
                    '{linesLabel}',
                    t('master_document.createFromSource.lineNoun', {
                      count: s.selectedLineIds.length,
                    }),
                  )
                  .replaceAll(
                    '{sourcesLabel}',
                    t('master_document.createFromSource.sourceNoun', {
                      count: s.selectedSourceIds.length,
                    }),
                  )}
              </p>
              {(config.confirmationStep?.effects ?? []).length > 0 && (
                <ul className="flex flex-col gap-2">
                  {config.confirmationStep!.effects!.map((eff) => (
                    <li key={eff.key} className="text-sm text-content-secondary dark:text-dark-content-secondary flex flex-col">
                      <span className="font-medium text-content-primary dark:text-dark-content-primary">{eff.label}</span>
                      {eff.description && <span className="text-xs">{eff.description}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {s.submitError && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 dark:bg-red-950/20 dark:border-red-800 dark:text-red-300" role="alert">
              {s.submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border-primary dark:border-dark-border-primary">
          <div>
            {s.canGoBack && (
              <button
                type="button"
                onClick={s.back}
                disabled={s.submitting}
                className="px-4 py-2 rounded-md text-sm font-semibold border border-border-primary text-content-tertiary hover:bg-background-secondary transition-all duration-150 dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary"
              >
                {i18n?.backButton ?? t('master_document.chrome.back')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={s.submitting}
              className={clsx(
                'px-4 py-2 rounded-md text-sm font-semibold border border-border-primary text-content-tertiary hover:bg-background-secondary transition-all duration-150',
                'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
                s.submitting && 'opacity-50 cursor-not-allowed',
              )}
            >
              {i18n?.cancelButton ?? t('master_document.chrome.cancel')}
            </button>
            {s.step !== 'origin' && (
              <button
                type="button"
                onClick={s.next}
                disabled={!s.canAdvance || s.submitting}
                className={clsx(
                  'flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold transition-all duration-150',
                  'bg-primary-inverse-background text-white hover:bg-primary-custom-700',
                  'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
                  (!s.canAdvance || s.submitting) && 'opacity-50 cursor-not-allowed',
                )}
              >
                {s.submitting && (
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="w-4 h-4 animate-spin">
                    <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd" />
                  </svg>
                )}
                {s.step === 'confirm' || (s.step === 'lines' && !s.hasConfirmStep)
                  ? (i18n?.confirmButton ?? t('master_document.chrome.confirm'))
                  : (i18n?.nextButton ?? t('master_document.chrome.next'))}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
